import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import api from "../lib/api";
import { Megaphone, Sparkles, ChevronLeft, ChevronRight, ArrowRight } from "lucide-react";

/**
 * EventBoard — bold, glowing announcement carousel shown on the Home page
 * between the vault hero and the Apex packages. Admin-controlled via
 * /api/admin/event-board. Cross-fades between slides every 10s and hides
 * itself entirely when no active events exist.
 */
function Slide({ item, active }) {
  return (
    <div
      className={`transition-opacity duration-700 ease-out ${
        active ? "opacity-100 relative" : "opacity-0 absolute inset-0 pointer-events-none"
      }`}
      aria-hidden={!active}
    >
      <div className="flex items-center gap-2 flex-wrap mb-2">
        <h3
          data-testid={active ? "event-board-title" : undefined}
          className="font-serif text-2xl sm:text-3xl font-bold text-white"
        >
          {item.title}
        </h3>
        {item.cashback_percent > 0 && (
          <span
            data-testid={active ? "event-board-badge" : undefined}
            className="inline-flex items-center gap-1 px-3 py-1 rounded-full text-[11px] font-bold uppercase tracking-wider bg-gradient-to-r from-[#9400D3] to-[#d4af37] text-[#0b0c10] shadow-[0_0_20px_-5px_rgba(212,175,55,0.75)]"
          >
            <Sparkles className="w-3 h-3" />
            {item.cashback_percent}% Cashback
          </span>
        )}
      </div>
      <p
        data-testid={active ? "event-board-body" : undefined}
        className="text-sm sm:text-base text-[var(--av-text-dim)] max-w-2xl whitespace-pre-line"
      >
        {item.body}
      </p>
      {item.cta_label && item.cta_href && (
        <div className="mt-4">
          {/^https?:\/\//i.test(item.cta_href) ? (
            <a
              data-testid={active ? "event-board-cta" : undefined}
              href={item.cta_href}
              target="_blank"
              rel="noopener noreferrer"
              className="btn-gold inline-flex items-center gap-2 text-sm"
              tabIndex={active ? 0 : -1}
            >
              {item.cta_label} <ArrowRight className="w-3.5 h-3.5" />
            </a>
          ) : (
            <Link
              data-testid={active ? "event-board-cta" : undefined}
              to={item.cta_href}
              className="btn-gold inline-flex items-center gap-2 text-sm"
              tabIndex={active ? 0 : -1}
            >
              {item.cta_label} <ArrowRight className="w-3.5 h-3.5" />
            </Link>
          )}
        </div>
      )}
    </div>
  );
}

export default function EventBoard() {
  const [items, setItems] = useState([]);
  const [idx, setIdx] = useState(0);
  const [maxH, setMaxH] = useState(0);
  const measureRefs = useRef([]);

  useEffect(() => {
    api.get("/event-board").then(r => setItems(r.data || [])).catch(() => setItems([]));
  }, []);

  useEffect(() => {
    if (items.length <= 1) return;
    const iv = setInterval(() => setIdx(i => (i + 1) % items.length), 10000);
    return () => clearInterval(iv);
  }, [items.length]);

  // Lock the slide container to the tallest slide so cross-fading between
  // shorter/longer copy doesn't jank the surrounding layout.
  useEffect(() => {
    if (!items.length) return;
    const measure = () => {
      const heights = measureRefs.current.map(el => (el ? el.offsetHeight : 0));
      const h = Math.max(0, ...heights);
      if (h) setMaxH(h);
    };
    measure();
    window.addEventListener("resize", measure);
    return () => window.removeEventListener("resize", measure);
  }, [items]);

  if (!items.length) return null;
  const safeIdx = idx % items.length;
  const prev = () => setIdx(i => (i - 1 + items.length) % items.length);
  const next = () => setIdx(i => (i + 1) % items.length);

  return (
    <section
      id="event-board"
      data-testid="event-board"
      aria-label="Live events and offers"
      className="px-4 sm:px-8 max-w-7xl mx-auto -mt-6 mb-12 scroll-mt-28"
    >
      <div className="relative overflow-hidden rounded-2xl border border-[var(--av-gold)]/40 bg-gradient-to-br from-[#1a1120] via-[#0e0b18] to-[#120a20] shadow-[0_0_45px_-15px_rgba(148,0,211,0.55),0_0_30px_-15px_rgba(212,175,55,0.55)]">
        <div className="pointer-events-none absolute -top-24 -left-24 w-72 h-72 rounded-full bg-[var(--av-gold)]/15 blur-3xl" />
        <div className="pointer-events-none absolute -bottom-24 -right-24 w-72 h-72 rounded-full bg-[var(--av-purple)]/25 blur-3xl" />

        <div className="relative p-5 sm:p-7 md:p-8">
          <div className="flex items-center gap-2 mb-3">
            <Megaphone className="w-4 h-4 text-[var(--av-gold)]" />
            <span className="mono-caption text-[var(--av-gold)]">Live from the Vault</span>
          </div>

          <div className="grid md:grid-cols-[1fr_auto] gap-4 md:gap-6 items-center">
            {/* Slide viewport — height locked to tallest slide for smooth cross-fade */}
            <div
              className="relative"
              style={maxH ? { minHeight: `${maxH}px` } : undefined}
            >
              {items.map((it, i) => (
                <Slide key={it.id} item={it} active={i === safeIdx} />
              ))}
              {/* Hidden measurement layer — renders every slide at natural
                  height so we can size the viewport to the tallest one
                  without visual glitches. */}
              <div className="invisible absolute inset-0 pointer-events-none" aria-hidden="true">
                {items.map((it, i) => (
                  <div key={`m-${it.id}`} ref={el => (measureRefs.current[i] = el)}>
                    <Slide item={it} active />
                  </div>
                ))}
              </div>
            </div>

            {items.length > 1 && (
              <div className="flex items-center gap-3 md:justify-end">
                <button
                  data-testid="event-board-prev"
                  onClick={prev}
                  aria-label="Previous event"
                  className="w-8 h-8 rounded-full border border-[var(--av-border)] text-[var(--av-text-dim)] hover:text-[var(--av-gold)] hover:border-[var(--av-gold)]/50 flex items-center justify-center transition-colors"
                >
                  <ChevronLeft className="w-3.5 h-3.5" />
                </button>
                <div className="flex items-center gap-2" role="tablist">
                  {items.map((item, i) => {
                    const isActive = i === safeIdx;
                    return (
                      <button
                        key={item?.id ?? item?.key ?? `dot-${i}`}
                        data-testid={`event-board-dot-${i}`}
                        onClick={() => setIdx(i)}
                        role="tab"
                        aria-selected={isActive}
                        aria-label={`Event ${i + 1} of ${items.length}`}
                        className="group relative w-4 h-4 flex items-center justify-center"
                      >
                        <span
                          className={`block rounded-full transition-all duration-500 ease-out ${
                            isActive
                              ? "w-1.5 h-1.5 bg-[var(--av-gold)] shadow-[0_0_8px_rgba(212,175,55,0.65)]"
                              : "w-1 h-1 bg-[var(--av-text-muted)]/50 group-hover:bg-[var(--av-text-muted)]"
                          }`}
                        />
                      </button>
                    );
                  })}
                </div>
                <button
                  data-testid="event-board-next"
                  onClick={next}
                  aria-label="Next event"
                  className="w-8 h-8 rounded-full border border-[var(--av-border)] text-[var(--av-text-dim)] hover:text-[var(--av-gold)] hover:border-[var(--av-gold)]/50 flex items-center justify-center transition-colors"
                >
                  <ChevronRight className="w-3.5 h-3.5" />
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}
